const DAY_MS = 24 * 60 * 60 * 1000;

export const TIME_RANGES = [
  { id: 'all', label: 'Tüm zamanlar', windowMs: null },
  { id: 'month', label: 'Bu ay', windowMs: 30 * DAY_MS },
  { id: 'week', label: 'Bu hafta', windowMs: 7 * DAY_MS },
  { id: 'day', label: 'Bugün', windowMs: DAY_MS },
];

export const PLATFORM_META = {
  facebook: {
    id: 'facebook',
    label: 'Facebook',
    short: 'FB',
    className: 'bg-sky-500/10 text-sky-300 ring-sky-500/30',
    color: '#38bdf8',
  },
  instagram: {
    id: 'instagram',
    label: 'Instagram',
    short: 'IG',
    className: 'bg-fuchsia-500/10 text-fuchsia-300 ring-fuchsia-500/30',
    color: '#e879f9',
  },
  voice: {
    id: 'voice',
    label: 'Telefon',
    short: 'Tel',
    className: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
    color: '#34d399',
  },
  tiktok: {
    id: 'tiktok',
    label: 'TikTok',
    short: 'TT',
    className: 'bg-zinc-500/15 text-zinc-100 ring-zinc-400/30',
    color: '#22d3ee',
  },
  whatsapp: {
    id: 'whatsapp',
    label: 'WhatsApp',
    short: 'WA',
    className: 'bg-emerald-500/10 text-emerald-300 ring-emerald-500/30',
    color: '#25d366',
  },
};

export const INTENT_LABELS = {
  randevu_talebi: 'Randevu talebi',
  fiyat_sorgusu: 'Fiyat sorgusu',
  bilgi_talebi: 'Bilgi talebi',
  sikayet: 'Şikayet',
  diger: 'Diğer',
  unknown: 'Belirsiz',
};

export const SENTIMENT_LABELS = {
  positive: 'Olumlu',
  neutral: 'Nötr',
  negative: 'Olumsuz',
  unknown: 'Belirsiz',
};

const PLATFORM_ALIASES = {
  facebook: 'facebook',
  fb: 'facebook',
  messenger: 'facebook',
  instagram: 'instagram',
  ig: 'instagram',
  voice: 'voice',
  phone: 'voice',
  elevenlabs: 'voice',
  call: 'voice',
  tiktok: 'tiktok',
  tt: 'tiktok',
  whatsapp: 'whatsapp',
  wa: 'whatsapp',
};

export function normalizePlatform(value) {
  const key = String(value || 'facebook').trim().toLowerCase();
  return PLATFORM_ALIASES[key] || key || 'facebook';
}

export function platformMeta(value) {
  const id = normalizePlatform(value);
  return (
    PLATFORM_META[id] || {
      id,
      label: id,
      short: id.slice(0, 3).toUpperCase(),
      className: 'bg-slate-500/10 text-slate-300 ring-slate-500/30',
      color: '#94a3b8',
    }
  );
}

export function conversationKey(summary) {
  return `${normalizePlatform(summary.platform)}:${String(summary.externalUserId || '').trim()}`;
}

export function summaryTimestamp(summary) {
  const raw = summary.windowEnd || summary.createdAt;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Newest version per (platform, externalUserId). */
export function latestPerConversation(summaries = []) {
  const byKey = new Map();
  for (const summary of summaries) {
    const key = conversationKey(summary);
    if (key.endsWith(':')) continue;
    const existing = byKey.get(key);
    if (
      !existing ||
      Number(summary.version || 0) > Number(existing.version || 0) ||
      (Number(summary.version || 0) === Number(existing.version || 0) &&
        summaryTimestamp(summary) > summaryTimestamp(existing))
    ) {
      byKey.set(key, { ...summary, platform: normalizePlatform(summary.platform) });
    }
  }
  return Array.from(byKey.values()).sort((a, b) => summaryTimestamp(b) - summaryTimestamp(a));
}

/** Group newest + older versions for Görüşme Özetleri cards. */
export function groupSummariesByConversation(summaries = []) {
  const sorted = [...summaries].sort((a, b) => {
    const versionDiff = Number(b.version || 0) - Number(a.version || 0);
    if (versionDiff !== 0) return versionDiff;
    return summaryTimestamp(b) - summaryTimestamp(a);
  });

  const byKey = new Map();
  for (const summary of sorted) {
    const key = conversationKey(summary);
    if (key.endsWith(':')) continue;
    const normalized = { ...summary, platform: normalizePlatform(summary.platform) };
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, { key, latest: normalized, history: [] });
    } else {
      existing.history.push(normalized);
    }
  }

  return Array.from(byKey.values()).sort(
    (a, b) => summaryTimestamp(b.latest) - summaryTimestamp(a.latest)
  );
}

const TR_MOBILE_IN_TEXT =
  /(?:\+?90|00\s*90|0)?[\s().-]*5\d{2}[\s().-]*\d{3}[\s().-]*\d{2}[\s().-]*\d{2}/g;
const GENERIC_PHONE_IN_TEXT = /\+?\d[\d\s().-]{8,14}\d/g;
const CHANNEL_IDS_ARE_NOT_PHONES = new Set(['facebook', 'instagram', 'tiktok']);

export function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

export function looksLikePhoneNumber(value, context = {}) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const digits = phoneDigits(raw);
  if (digits.length < 10 || digits.length > 13) return false;
  const extDigits = phoneDigits(context.externalUserId);
  if (
    extDigits &&
    digits === extDigits &&
    CHANNEL_IDS_ARE_NOT_PHONES.has(normalizePlatform(context.platform))
  ) {
    return false;
  }
  return true;
}

function firstPhoneMatch(text) {
  const source = String(text || '');
  if (!source) return null;
  TR_MOBILE_IN_TEXT.lastIndex = 0;
  GENERIC_PHONE_IN_TEXT.lastIndex = 0;
  const matches = source.match(TR_MOBILE_IN_TEXT) || source.match(GENERIC_PHONE_IN_TEXT) || [];
  for (const match of matches) {
    const trimmed = String(match).trim();
    if (looksLikePhoneNumber(trimmed)) return trimmed;
  }
  return null;
}

export function formatPhoneDisplay(value) {
  let digits = phoneDigits(value);
  if (!digits) return '';
  if (digits.startsWith('90') && digits.length === 12) digits = digits.slice(2);
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  if (digits.length === 10 && digits.startsWith('5')) {
    return `0 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;
  }
  return String(value).trim();
}

export function telHref(value) {
  let digits = phoneDigits(value);
  if (!digits) return '';
  if (digits.startsWith('0') && digits.length === 11) digits = `90${digits.slice(1)}`;
  if (digits.length === 10 && digits.startsWith('5')) digits = `90${digits}`;
  return `tel:+${digits}`;
}

export function stripPhonesFromText(text) {
  TR_MOBILE_IN_TEXT.lastIndex = 0;
  return String(text || '')
    .replace(TR_MOBILE_IN_TEXT, ' ')
    .replace(/\(\s*\)/g, ' ')
    .replace(/\s+[–—-]\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();
}

/** Prefer the structured phone field, then numbers written into next_action / summary. */
export function resolveSummaryPhone(summary) {
  if (!summary) return null;
  const context = { platform: summary.platform, externalUserId: summary.externalUserId };
  const candidates = [
    summary.phone,
    firstPhoneMatch(summary.nextAction),
    firstPhoneMatch(summary.summary),
  ];
  for (const candidate of candidates) {
    if (looksLikePhoneNumber(candidate, context)) return phoneDigits(candidate);
  }
  return null;
}

export function inheritSummaryPhone(latest, history = []) {
  return resolveSummaryPhone(latest) || history.map(resolveSummaryPhone).find(Boolean) || null;
}

export function chartEntriesFromMap(map = {}, labelFn = (k) => k, colorFn = () => '#4a9e8a') {
  return Object.entries(map)
    .map(([key, value]) => ({
      name: labelFn(key),
      key,
      value,
      color: colorFn(key),
    }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
}
